import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import classnames from 'classnames';
import { toast } from 'react-toastify';

import restFactory from 'v2/utils/restFactory';
import styles from './responseReviewerSelect.module.scss';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import TextArea from 'v2/form/TextArea/TextArea';
import {
  SingleValue,
  OptionType,
  GeneralSelect,
} from 'v2/form/CustomSelectors/GeneralSelect';
import { SearchEntity } from 'v2/models';
import { useHistory, useLocation } from 'react-router-dom';
import { AxiosError } from 'axios';
import { useMutation } from 'react-query';
import { useFeatureFlagContext } from 'v2/context/featureFlagContext';
import { FEATURE_FLAGS } from 'utils/constants';

interface ReviewerResponseData {
  respondMessage: string;
}

interface ResponseReviewerSelectProps {
  search: SearchEntity;
  disabled?: boolean;
  onSubmit?: () => void;
  onError?: (error: string) => void;
  onComplete?: () => void;
}

const respondToOriginatorOption: OptionType = {
  value: 'Respond to Originator',
  label: 'Respond to Originator',
};

const linkEntityOption: OptionType = {
  value: 'Link Entity',
  label: 'Link Entity',
};

function getEditSearchOption(search: SearchEntity) {
  return {
    value: `/edit/${search.id}`,
    label: 'Edit Request',
  };
}

export function getOptions(search: SearchEntity) {
  const editSearchOption = getEditSearchOption(search);
  const options: OptionType[] = [editSearchOption];

  if (search.reviewer_response) {
    options.push({
      value: `/reviewer/request-details/${search.id}/conversations`,
      label: 'View conversation',
    });
  } else {
    options.push(respondToOriginatorOption);
  }

  options.push(linkEntityOption);
  return options;
}

const ResponseReviewerSelect = ({
  search,
  onComplete,
  disabled,
  onSubmit,
  onError,
}: ResponseReviewerSelectProps) => {
  const [showResponseModal, setShowResponseModal] = useState(false);
  const { flags } = useFeatureFlagContext();
  const isAutoAssignEnabled = flags && flags[FEATURE_FLAGS.REVIEWER_ASSIGNMENT];
  const location = useLocation();
  const history = useHistory();
  const { handleSubmit, register } = useForm<ReviewerResponseData>({
    mode: 'onChange',
  });
  const [selectedOption, setSelectedOption] = useState<SingleValue<OptionType>>(
    { value: '', label: '' }
  );

  const options: OptionType[] = getOptions(search);

  const handleLinkEntitySelected = () => {
    history.push(`/reviewer/request-details/${search.id}/detail`, {
      counterToGoBack: -1,
      comingFromPath: location.pathname,
      linkEntityModalOpen: true,
    });
  };

  const handleChange = (selectedOption: SingleValue<OptionType>) => {
    setSelectedOption(selectedOption);
    const editSearchOption = getEditSearchOption(search);
    if (selectedOption?.value === respondToOriginatorOption.value) {
      setShowResponseModal(true);
    } else if (selectedOption?.value === linkEntityOption.value) {
      handleLinkEntitySelected();
    } else if (selectedOption?.value === editSearchOption.value) {
      history.replace(selectedOption.value);
    } else if (selectedOption?.value) {
      history.push(selectedOption.value as string);
    }
  };

  const { mutate: createConversation, isLoading: isCreateConversationLoading } =
    useMutation(
      (data: ReviewerResponseData) =>
        restFactory.post('/conversations/create_reviewer_conversation', {
          search: search.id,
          message: data.respondMessage,
        }),
      {
        onMutate: async () => {
          onSubmit?.();
          if (isAutoAssignEnabled) {
            await restFactory.get(
              `/dj/api/v1/search/${search.id}/assign_to_reviewer/`,
              { action: 'assign' }
            );
          }
        },
        onSuccess: () => {
          toast.success('Response sent');
        },
        onError: (error: AxiosError | string) => {
          const errorMessage =
            typeof error === 'string'
              ? error
              : 'Internal Error Occurred. Please try again later.';
          toast.error(errorMessage);
          onError?.(errorMessage);
        },
        onSettled: () => {
          onComplete?.();
          closeModalSend();
        },
      }
    );

  const responseModalSend = async (values: ReviewerResponseData) => {
    createConversation(values);
  };

  const closeModalSend = () => {
    setShowResponseModal(false);
    setSelectedOption({ value: '', label: '' });
  };

  return (
    <>
      <GeneralSelect
        options={options}
        handleChange={handleChange}
        selectedOption={selectedOption}
        placeHolder="Select Action"
        disabled={disabled || isCreateConversationLoading}
      />
      <ConfirmationModal
        className={styles.viewModals}
        centered={true}
        animation={true}
        show={showResponseModal}
        onCancel={() => closeModalSend()}
        okBtnText="Send"
        onOk={handleSubmit(responseModalSend)}
        title="Send a Message to the Originator"
        isLoading={isCreateConversationLoading}
      >
        <>
          <span className="text-light3">
            Please provide your note to the originator below:
          </span>
          <TextArea
            data-testid="response-reviewer-message-box"
            label=""
            className={classnames('mt-3', styles.responseText)}
            register={register('respondMessage', { required: true })}
            autoComplete="off"
            required={true}
          />
        </>
      </ConfirmationModal>
    </>
  );
};

export default ResponseReviewerSelect;
